import React from 'react';
import { observer } from 'mobx-react';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import { Select } from '@this/components/shared/ui/inputs/select';
import { FormControl, InputLabel } from '@this/shared/ui/inputs/form_control';
import type Country from '@this/domain/god/country';

function getStyles() {
  return {
    buttonRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      fontSize: '14px',
      marginTop: '10px'
    } as React.CSSProperties
  } as const;
}

interface ForeignRegionOption {
  id: number;
  name: string;
}

interface CountryFormProps {
  country: Country;
  foreignRegions: ForeignRegionOption[];
  onSubmit: () => void;
  onCancel: () => void;
  error: string | null;
  submitLabel: string;
  cancelLabel: string;
}

const CountryForm: React.FC<CountryFormProps> = ({
  country,
  foreignRegions,
  onSubmit,
  onCancel,
  error,
  submitLabel,
  cancelLabel
}) => {
  const styles = getStyles();

  return (
    <div>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="foreignRegionId">海外地域 *</InputLabel>
        <Select
          id="foreignRegionId"
          value={country.foreignRegionId?.toString() || ''}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
            const value = e.target.value as string;
            if (value) {
              const selectedRegion = foreignRegions.find(r => r.id === Number(value));
              country.foreignRegionId = Number(value);
              country.foreignRegionName = selectedRegion?.name || null;
            } else {
              country.foreignRegionId = null;
              country.foreignRegionName = null;
            }
          }}
        >
          <option value="">選択してください</option>
          {foreignRegions.map(region => (
            <option key={region.id} value={region.id}>
              {region.name}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="foreignRegionCode">海外地域コード</InputLabel>
        <Input
          id="foreignRegionCode"
          value={country.foreignRegionCode || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            country.foreignRegionCode = e.target.value || null;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="name">国名 *</InputLabel>
        <Input
          id="name"
          value={country.name || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            country.name = e.target.value;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="formalName">正式国名 *</InputLabel>
        <Input
          id="formalName"
          value={country.formalName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            country.formalName = e.target.value;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="enName">英語名</InputLabel>
        <Input
          id="enName"
          value={country.enName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            country.enName = e.target.value || null;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="iso2code">ISO2コード</InputLabel>
        <Input
          id="iso2code"
          value={country.iso2code || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            country.iso2code = e.target.value || null;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="ministryOfForeignAffairsId">外務省ID</InputLabel>
        <Input
          id="ministryOfForeignAffairsId"
          value={country.ministryOfForeignAffairsId || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            country.ministryOfForeignAffairsId = e.target.value || null;
          }}
        />
      </FormControl>
      {error && <div style={{ ...styles.error, whiteSpace: 'pre-wrap' }}>{error}</div>}
      <div style={styles.buttonRow}>
        <Button onClick={onSubmit}>{submitLabel}</Button>
        <Button color="sub" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
};

export default observer(CountryForm);
